import { useEffect, useRef } from 'react'
import { useSavePeriodReportComments } from '../../fetchData/mutations/useSavePeriodReportComments'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'

interface UseCommentUpdaterProps {
  selectedPeriodReportId: string | null
  isPeriodReportEditable: boolean
}

/**
 * Simple hook for updating comments with debouncing.
 */
export const useCommentUpdater = ({
  selectedPeriodReportId,
  isPeriodReportEditable,
}: UseCommentUpdaterProps) => {
  const { selectedPeriodReport } = useProjektprognosEditingContext()
  const saveMutation = useSavePeriodReportComments(selectedPeriodReportId)

  const pendingCommentsRef = useRef<Map<number, string>>(new Map())
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const updateComment = (accountId: number, text: string) => {
    if (!selectedPeriodReportId || !isPeriodReportEditable) return

    // Update map
    if (text !== undefined) {
      pendingCommentsRef.current.set(accountId, text)
    }

    // Clear and restart timer
    if (timerRef.current) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(() => {
      const comments = Array.from(pendingCommentsRef.current.entries()).map(
        ([id, text]) => ({ id, text })
      )

      if (comments.length > 0) {
        saveMutation.mutate({
          reportComment: selectedPeriodReport?.comments?.reportComment || '',
          accountComments: comments,
        })
      }
      pendingCommentsRef.current.clear()
    }, 600)
  }

  return {
    updateComment,
    isSaving: saveMutation.isPending,
    hasError: saveMutation.isError,
  }
}
